import type { CatalogEntry } from '../types/catalog';
import type { Vendor } from '../types/drive';

export function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

export function modelItemHtml(model: CatalogEntry, selectedModelCode: string | null): string {
  const selected = selectedModelCode === model.modelCode ? 'selected' : '';
  return `
          <div class="catalog-model-item ${selected}" data-model="${escapeHtml(model.modelCode)}">
            <div class="catalog-model-name">${escapeHtml(model.name)}</div>
            <div class="catalog-model-meta">${escapeHtml(model.modelCode)} | ${model.powerKW} kW | ${model.nominalCurrent} A | ${model.voltageClass}</div>
          </div>
        `;
}

export function renderSpecsCard(entry: CatalogEntry): string {
  const coolingLabel =
    entry.coolingMethod === 'forced-air'
      ? 'Ar Forçado'
      : entry.coolingMethod === 'liquid'
        ? 'Líquido'
        : 'Natural';

  return `
      <div class="specs-title">Especificações Técnicas</div>
      <div class="specs-grid">
        <div class="spec-item"><span class="spec-label">Fabricante</span><span class="spec-value">${escapeHtml(entry.vendor)}</span></div>
        <div class="spec-item"><span class="spec-label">Família</span><span class="spec-value">${escapeHtml(entry.family)}</span></div>
        <div class="spec-item"><span class="spec-label">Código</span><span class="spec-value">${escapeHtml(entry.modelCode)}</span></div>
        <div class="spec-item"><span class="spec-label">Potência</span><span class="spec-value">${entry.powerKW} kW</span></div>
        <div class="spec-item"><span class="spec-label">Corrente Nominal</span><span class="spec-value">${entry.nominalCurrent} A</span></div>
        ${entry.nominalCurrentND !== null ? `<div class="spec-item"><span class="spec-label">Corrente ND</span><span class="spec-value">${entry.nominalCurrentND} A</span></div>` : ''}
        ${entry.nominalCurrentHD !== null ? `<div class="spec-item"><span class="spec-label">Corrente HD</span><span class="spec-value">${entry.nominalCurrentHD} A</span></div>` : ''}
        <div class="spec-item"><span class="spec-label">Tensão</span><span class="spec-value">${entry.voltageClass}</span></div>
        <div class="spec-item"><span class="spec-label">Frequência</span><span class="spec-value">${entry.ratedFrequency} Hz</span></div>
        <div class="spec-item"><span class="spec-label">Refrigeração</span><span class="spec-value">${coolingLabel}</span></div>
        <div class="spec-item"><span class="spec-label">Proteção</span><span class="spec-value">${escapeHtml(entry.protectionRating)}</span></div>
        <div class="spec-item"><span class="spec-label">Ventiladores</span><span class="spec-value">${entry.fanCount}</span></div>
        <div class="spec-item"><span class="spec-label">Temp. Máx.</span><span class="spec-value">${entry.maxTemp} °C</span></div>
        <div class="spec-item"><span class="spec-label">Barramento CC</span><span class="spec-value">${entry.nominalDC} V</span></div>
        ${entry.partNumber ? `<div class="spec-item"><span class="spec-label">Nº Peça</span><span class="spec-value">${escapeHtml(entry.partNumber)}</span></div>` : ''}
        ${entry.frameSize ? `<div class="spec-item"><span class="spec-label">Tamanho Chassi</span><span class="spec-value">${escapeHtml(entry.frameSize)}</span></div>` : ''}
        ${entry.orderingNumber ? `<div class="spec-item"><span class="spec-label">Nº Pedido</span><span class="spec-value">${escapeHtml(entry.orderingNumber)}</span></div>` : ''}
        ${entry.powerCardCode ? `<div class="spec-item"><span class="spec-label">Power Card</span><span class="spec-value">${escapeHtml(entry.powerCardCode)}</span></div>` : ''}
        ${entry.igbtModuleCode ? `<div class="spec-item"><span class="spec-label">Módulo IGBT</span><span class="spec-value">${escapeHtml(entry.igbtModuleCode)}</span></div>` : ''}
        ${entry.fanCode ? `<div class="spec-item"><span class="spec-label">Ventilador</span><span class="spec-value">${escapeHtml(entry.fanCode)}</span></div>` : ''}
        ${entry.capacitorBankCode ? `<div class="spec-item"><span class="spec-label">Capacitores</span><span class="spec-value">${escapeHtml(entry.capacitorBankCode)}</span></div>` : ''}
      </div>
      <div class="specs-rul-title">Vida Útil dos Componentes</div>
      <div class="specs-grid">
        <div class="spec-item"><span class="spec-label">Ventilador Principal</span><span class="spec-value">${entry.rulParams.fanMainLife.toLocaleString()} h</span></div>
        <div class="spec-item"><span class="spec-label">Ventilador Controle</span><span class="spec-value">${entry.rulParams.fanControlLife.toLocaleString()} h</span></div>
        <div class="spec-item"><span class="spec-label">Capacitor DC Link</span><span class="spec-value">${entry.rulParams.capacitorBaseLife.toLocaleString()} h</span></div>
        <div class="spec-item"><span class="spec-label">IGBTs</span><span class="spec-value">${entry.rulParams.igbtMaxCycles.toLocaleString()} ciclos</span></div>
        <div class="spec-item"><span class="spec-label">Rolamentos</span><span class="spec-value">${entry.rulParams.bearingBaseLife.toLocaleString()} h</span></div>
      </div>
    `;
}

export function renderSelectModelButtonHtml(entry: CatalogEntry | undefined): string {
  if (!entry) return '';
  return `<button id="btnSelectModel" class="btn btn-primary btn-select-model" data-model="${escapeHtml(entry.modelCode)}">Selecionar este modelo</button>`;
}

export function renderCatalogListHtml(
  catalogs: Array<{ key: string; vendor: string; family: string; models: number }>,
  pendingCount: number,
  supabaseOk: boolean,
): string {
  if (catalogs.length === 0) {
    return '<div class="catalog-list-empty">Nenhum catálogo importado.</div>';
  }
  return `
      <div class="catalog-list-actions">
        <button id="btnExportAllCatalogs" class="btn btn-sm btn-primary">Exportar Tudo</button>
        ${pendingCount > 0
          ? `<span class="catalog-list-pending">${pendingCount} pendente(s)</span>`
          : ''}
        ${supabaseOk ? '<button id="btnMigrateToCloud" class="btn btn-sm btn-secondary">Migrar para nuvem</button>' : ''}
      </div>
      ${catalogs.map(({ key, vendor, family, models }) => `
        <div class="catalog-list-item">
          <div class="catalog-list-info">
            <span class="catalog-list-vendor">${escapeHtml(vendor)}</span>
            <span class="catalog-list-family">${escapeHtml(family)}</span>
            <span class="catalog-list-meta">${models} modelos</span>
          </div>
          <button class="btn-remove-catalog" data-key="${escapeHtml(key)}">Remover</button>
        </div>
      `).join('')}
    `;
}

export function vendorTabHtml(vendor: Vendor | 'ALL', current: Vendor | 'ALL'): string {
  const active = current === vendor ? 'active' : '';
  const label = vendor === 'ALL' ? 'Todos' : vendor;
  return `
        <button class="catalog-tab ${active}" data-vendor="${vendor}">${label}</button>
      `;
}